import type { ReactNode } from 'react'
import {
  IconeAlerta,
  IconeCamadas,
  IconePausar,
  IconePosicao,
  IconeReiniciar,
  IconeReproduzir,
  IconeRota,
  IconeVitima,
} from '../icones'
import type { FerramentasModulo } from './tipos'

export interface PropsExtracao {
  area: 'retirada' | 'oficina'
  aoEscolherArea: (area: 'retirada' | 'oficina') => void
  painelOficina: () => ReactNode
  painelDecisao?: () => ReactNode
  painelVitimas: () => ReactNode
  painelPosicao: () => ReactNode
  painelRota: () => ReactNode
  painelObstaculos: () => ReactNode
  painelTecnica: () => ReactNode
  painelProcedimentos: () => ReactNode
  /** Fase 5B — "preparar aula": autoria de cenário pelo professor */
  painelAutoria?: () => ReactNode
  painelSobreposicoes: () => ReactNode
  painelReproducao: () => ReactNode
  painelResultados: () => ReactNode
  quantidadeVitimas: number
  quantidadeObstaculos: number
  modoRota: string
  desvioGraus: number
  prioridadePendente: boolean
  modoExperiencia: 'demonstracao' | 'cenario'
  etapaDecisao: string
  reproduzindo: boolean
  aoAlternarReproducao: () => void
  aoReiniciar: () => void
  /** Vítima em retirada — agora também escolhida tocando no corpo na cena. */
  vitimaEmFoco: {
    id: string
    nome: string
    estadoClinico: string
    retencao: string
    posicao: string
    abertura: string
    procedimentosFeitos: number
    procedimentosTotal: number
  } | null
}

/**
 * Extração de vítima: vítimas, posição, rota, obstáculos,
 * sobreposições e reprodução.
 */
export function ferramentasExtracao(p: PropsExtracao): FerramentasModulo {
  const seletor = (
    <div role="group" aria-label="Área da Extração" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button type="button" className={`btn-acao-cad ${p.area === 'retirada' ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
        aria-pressed={p.area === 'retirada'} onClick={() => p.aoEscolherArea('retirada')}>
        <IconeRota />Retirada angulada
      </button>
      <button type="button" className={`btn-acao-cad ${p.area === 'oficina' ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
        aria-pressed={p.area === 'oficina'} onClick={() => p.aoEscolherArea('oficina')}>
        <IconeVitima />Oficina de Vítimas
      </button>
    </div>
  )
  if (p.area === 'oficina') {
    return {
      grupos: [{ id: 'oficina-vitimas', rotulo: 'Oficina de Vítimas', icone: <IconeVitima />, conteudo: p.painelOficina }],
      chips: [],
      inspetor: null,
      acoesRapidas: seletor,
    }
  }
  return {
    grupos: [
      {
        id: 'vitimas',
        rotulo: 'Vítimas',
        icone: <IconeVitima />,
        badge: p.quantidadeVitimas,
        conteudo: p.painelVitimas,
      },
      { id: 'posicao', rotulo: 'Posição', icone: <IconePosicao />, conteudo: p.painelPosicao },
      { id: 'rota', rotulo: 'Rota', icone: <IconeRota />, conteudo: p.painelRota },
      {
        id: 'obstaculos',
        rotulo: 'Obstáculos',
        icone: <IconeAlerta />,
        badge: p.quantidadeObstaculos,
        conteudo: p.painelObstaculos,
      },
      {
        id: 'sobreposicoes',
        rotulo: 'Sobreposições',
        icone: <IconeCamadas />,
        conteudo: p.painelSobreposicoes,
      },
      {
        id: 'reproducao',
        rotulo: 'Reprodução',
        icone: <IconeReproduzir />,
        conteudo: p.painelReproducao,
      },
    ],
    chips: [],
    /* Sem botão de fechar: a extração sempre tem alguém sendo retirado. */
    inspetor: p.vitimaEmFoco && {
      titulo: p.vitimaEmFoco.nome,
      subtitulo: 'Vítima em retirada',
      propriedades: [
        { rotulo: 'Estado clínico', valor: p.vitimaEmFoco.estadoClinico },
        { rotulo: 'Retenção', valor: p.vitimaEmFoco.retencao },
        { rotulo: 'Posição', valor: p.vitimaEmFoco.posicao },
        { rotulo: 'Saída', valor: p.vitimaEmFoco.abertura },
        { rotulo: 'Ângulo de retirada', valor: p.desvioGraus <= 0.5 ? '0° (retirada axial)' : `${p.desvioGraus.toFixed(0)}°` },
      ],
    },
    acoesRapidas: (
      <>
        {seletor}
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--primario"
          onClick={p.aoAlternarReproducao}
          title={p.reproduzindo ? 'Pausar reprodução' : 'Reproduzir rota de extração'}
        >
          {p.reproduzindo ? <><IconePausar />Pausar</> : <><IconeReproduzir />Executar Rota</>}
        </button>
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          onClick={p.aoReiniciar}
          title="Reiniciar rota"
        >
          <IconeReiniciar />Reiniciar
        </button>
      </>
    ),
  }
}